/* Step 1 — Plot dimensions + facing direction.
 *
 * Stays deliberately minimal: large tap targets, clear numeric inputs with
 * stepper buttons, facing-direction radio chips. Auto-validates that
 * customer's plot width × depth is in the 12-200 ft range our matcher
 * supports.
 */
import React from "react";
import { ChevronRight, Compass } from "lucide-react";

const FACINGS = [
  { id: "north", label: "उत्तर", english: "North" },
  { id: "south", label: "दक्षिण", english: "South" },
  { id: "east", label: "पूर्व", english: "East" },
  { id: "west", label: "पश्चिम", english: "West" },
];

const Stepper = ({ value, onChange, label, testId, min = 12, max = 200 }) => (
  <div className="rounded-2xl border-2 border-border bg-white p-4">
    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">{label}</div>
    <div className="mt-2 flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        data-testid={`${testId}-minus`}
        className="h-12 w-12 rounded-full bg-navy text-white text-xl font-extrabold hover:bg-navy-600"
      >
        −
      </button>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const v = parseInt(e.target.value || "0", 10);
          if (!Number.isFinite(v)) return;
          onChange(Math.max(min, Math.min(max, v)));
        }}
        data-testid={testId}
        className="flex-1 text-center text-3xl sm:text-4xl font-extrabold text-navy tabular-nums focus:outline-none"
      />
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        data-testid={`${testId}-plus`}
        className="h-12 w-12 rounded-full bg-navy text-white text-xl font-extrabold hover:bg-navy-600"
      >
        +
      </button>
      <span className="text-sm font-bold text-muted-foreground w-8">ft</span>
    </div>
  </div>
);

export default function StepPlot({ value, onChange, onNext }) {
  const area = value.plot_width * value.plot_depth;
  const ok = value.plot_width >= 12 && value.plot_depth >= 12;

  return (
    <section data-testid="studio-step-plot">
      <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">Step 1 of 3</div>
      <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold text-navy">Your plot dimensions</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Enter the plot's road-facing width and the depth.
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Stepper
          value={value.plot_width}
          onChange={(v) => onChange({ ...value, plot_width: v })}
          label="Plot Width (front)"
          testId="studio-plot-width"
        />
        <Stepper
          value={value.plot_depth}
          onChange={(v) => onChange({ ...value, plot_depth: v })}
          label="Plot Depth"
          testId="studio-plot-depth"
        />
      </div>

      {/* Quick visual */}
      <div className="mt-4 rounded-2xl bg-white border-2 border-border p-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Total area</div>
          <div className="text-2xl font-extrabold text-navy tabular-nums" data-testid="studio-plot-area">{area} sqft</div>
        </div>
        <div className="text-[11px] text-muted-foreground text-right max-w-[60%] leading-snug">
          {area < 600 && "Compact urban plot"}
          {area >= 600 && area < 1200 && "Standard town plot"}
          {area >= 1200 && area < 2000 && "Premium plot"}
          {area >= 2000 && "Luxury / multi-family"}
        </div>
      </div>

      {/* Facing */}
      <div className="mt-6 rounded-2xl bg-white border-2 border-border p-4">
        <div className="flex items-center gap-2">
          <Compass className="h-4 w-4 text-navy" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
            Plot is facing
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {FACINGS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onChange({ ...value, facing: f.id })}
              data-testid={`studio-facing-${f.id}`}
              className={`rounded-2xl border-2 px-3 py-3 text-left transition-colors ${
                value.facing === f.id
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy border-border hover:border-navy/40"
              }`}
            >
              <div className="text-base font-extrabold">{f.label}</div>
              <div className={`text-[11px] uppercase tracking-wider font-bold mt-0.5 ${
                value.facing === f.id ? "text-gold" : "text-muted-foreground"
              }`}>
                {f.english}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-7 flex justify-end">
        <button
          type="button"
          disabled={!ok}
          onClick={onNext}
          data-testid="studio-plot-next"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-gold font-extrabold hover:bg-navy-600 disabled:opacity-50"
        >
          Continue
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}
